import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { isAdmin, useAuth } from "../lib/auth";
import {
  addUtcDays,
  calendarWindow,
  defaultShiftWindow,
  fmtDayHeading,
  fmtMonth,
  fmtTime,
  fmtUntil,
  fmtWeekRange,
  fromDatetimeLocal,
  isSameUtcDay,
  overlapsDay,
  personColor,
  personLabel,
  startOfUtcMonth,
  startOfUtcWeek,
  toDatetimeLocal,
  WEEKDAYS,
  type DutyPerson,
  type DutyShift,
} from "../lib/vaktaskra";

export const Route = createFileRoute("/verkfaeri/vaktaskra")({
  component: VaktaskraPage,
});

type View = "week" | "month";

type FormState = {
  id: number | null;
  userId: string;
  startsAt: string;
  endsAt: string;
  note: string;
};

const card = "rounded-2xl border border-line bg-white p-5 shadow-sm";
const input =
  "mt-1 w-full rounded-lg border border-line bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-2 focus:ring-brand/20";
const label = "block text-[12.5px] font-semibold text-ink";

function emptyForm(people: DutyPerson[], day: Date): FormState {
  const window = defaultShiftWindow(day);
  return {
    id: null,
    userId: people[0]?.id ?? "",
    startsAt: toDatetimeLocal(window.startsAt),
    endsAt: toDatetimeLocal(window.endsAt),
    note: "",
  };
}

function fromShift(shift: DutyShift): FormState {
  return {
    id: shift.id,
    userId: shift.userId,
    startsAt: toDatetimeLocal(shift.startsAt),
    endsAt: toDatetimeLocal(shift.endsAt),
    note: shift.note ?? "",
  };
}

function VaktaskraPage() {
  const { user: me } = useAuth();
  const [view, setView] = useState<View>("week");
  const [cursor, setCursor] = useState(() => new Date());
  const [people, setPeople] = useState<DutyPerson[]>([]);
  const [onDuty, setOnDuty] = useState<DutyShift[]>([]);
  const [shifts, setShifts] = useState<DutyShift[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cal = useMemo(() => calendarWindow(view, cursor), [view, cursor]);
  const today = new Date();

  const reload = useCallback(() => {
    setError(null);
    const from = cal.from.toISOString();
    const to = cal.to.toISOString();
    setLoading(true);
    Promise.all([
      api.listDutyPeople(),
      api.listDutyNow(),
      api.listDutyShifts(from, to),
    ])
      .then(([nextPeople, nextOnDuty, nextShifts]) => {
        setPeople(nextPeople);
        setOnDuty(nextOnDuty);
        setShifts(nextShifts);
      })
      .catch((e: unknown) =>
        setError(e instanceof Error ? e.message : String(e)),
      )
      .finally(() => setLoading(false));
  }, [cal.from, cal.to]);

  useEffect(() => {
    reload();
  }, [reload]);

  function step(dir: number) {
    setCursor((c) =>
      view === "week"
        ? addUtcDays(startOfUtcWeek(c), dir * 7)
        : new Date(Date.UTC(c.getUTCFullYear(), c.getUTCMonth() + dir, 1)),
    );
  }

  async function save() {
    if (!form || !form.userId || !form.startsAt || !form.endsAt) return;
    setSaving(true);
    setError(null);
    const body = {
      userId: form.userId,
      startsAt: fromDatetimeLocal(form.startsAt),
      endsAt: fromDatetimeLocal(form.endsAt),
      note: form.note.trim() ? form.note.trim() : null,
    };
    try {
      if (form.id == null) {
        await api.createDutyShift(body);
      } else {
        await api.updateDutyShift(form.id, body);
      }
      setForm(null);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (form?.id == null) return;
    if (!confirm("Eyða þessari vakt?")) return;
    setSaving(true);
    setError(null);
    try {
      await api.deleteDutyShift(form.id);
      setForm(null);
      reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto max-w-[1180px] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[13px] font-bold uppercase tracking-[0.06em] text-brand">
            Vaktaskrá
          </div>
          <h1 className="m-0 mt-1 font-display text-[28px] font-extrabold tracking-tight text-ink">
            Hver er á vakt
          </h1>
          <p className="m-0 mt-1 max-w-[520px] text-[13.5px] leading-5 text-muted-2">
            Frávik fara á þá sem eru skráðir á vakt. Ef enginn er á vakt fer
            pósturinn á afritslistann í Stillingum.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setForm(emptyForm(people, today))}
          className="h-10 cursor-pointer rounded-xl bg-ink px-4 text-sm font-bold text-white"
        >
          Ný vakt
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-xl border border-danger-50 bg-danger-50 px-4 py-3 text-[13px] font-semibold text-danger">
          {error}
        </div>
      )}

      <section className={`${card} mb-5`}>
        <div className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-4">
          Á vakt núna
        </div>
        {onDuty.length === 0 ? (
          <p className="mt-2 text-[13.5px] text-muted">
            Enginn á vakt — frávik fara á afritslistann í Stillingum.
            {isAdmin(me) && (
              <>
                {" "}
                <Link
                  to="/admin/stillingar"
                  className="font-semibold text-brand"
                >
                  Opna Stillingar
                </Link>
              </>
            )}
          </p>
        ) : (
          <div className="mt-3 flex flex-wrap gap-2.5">
            {onDuty.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setForm(fromShift(s))}
                className="flex cursor-pointer items-center gap-3 rounded-2xl border border-line bg-canvas px-3.5 py-2.5 text-left"
              >
                <span
                  className="h-2.5 w-2.5 flex-none rounded-full"
                  style={{ background: personColor(s.userId) }}
                />
                <span>
                  <span className="block text-sm font-bold text-ink">
                    {personLabel(s)}
                  </span>
                  <span className="block text-[11.5px] text-muted-2">
                    til {fmtUntil(s.endsAt)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </section>

      <section className={card}>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => step(-1)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-line text-ink"
              aria-label="Fyrra"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-lg border border-line text-ink"
              aria-label="Næsta"
            >
              ›
            </button>
          </div>
          <div className="min-w-[220px] flex-1 font-display text-[18px] font-extrabold capitalize text-ink">
            {view === "week"
              ? fmtWeekRange(cal.from, cal.to)
              : fmtMonth(startOfUtcMonth(cursor))}
          </div>
          <div className="inline-flex rounded-[11px] bg-[#E8F1FF] p-1">
            {(["week", "month"] as const).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className="h-[34px] cursor-pointer rounded-lg px-[15px] text-[13px] font-bold transition-colors"
                style={
                  view === v
                    ? {
                        background: "#fff",
                        color: "#0B2447",
                        boxShadow: "0 2px 6px rgba(11,36,71,.12)",
                      }
                    : { background: "transparent", color: "#5B6B85" }
                }
              >
                {v === "week" ? "Vika" : "Mánuður"}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setCursor(new Date())}
            className="h-[34px] cursor-pointer rounded-lg border border-line px-3 text-[13px] font-semibold text-ink"
          >
            Í dag
          </button>
        </div>

        <div className="grid grid-cols-7 gap-px rounded-xl bg-line">
          {WEEKDAYS.map((d) => (
            <div
              key={d}
              className="bg-canvas px-2 py-2 text-center text-[11px] font-bold uppercase tracking-[0.04em] text-muted-2"
            >
              {d}
            </div>
          ))}
          {cal.days.map((day) => {
            const items = shifts.filter((s) => overlapsDay(s, day));
            const inMonth =
              view === "week" ||
              day.getUTCMonth() === startOfUtcMonth(cursor).getUTCMonth();
            const isToday = isSameUtcDay(day, today);
            return (
              <div
                key={day.toISOString()}
                onClick={() => setForm(emptyForm(people, day))}
                className={`min-h-[110px] cursor-pointer bg-white px-1.5 py-2 text-left align-top ${
                  inMonth ? "" : "opacity-40"
                } ${isToday ? "ring-1 ring-inset ring-brand" : ""}`}
              >
                <div
                  className={`mb-1.5 text-[12px] font-bold ${
                    isToday ? "text-brand" : "text-muted"
                  }`}
                >
                  {fmtDayHeading(day)}
                </div>
                <div className="flex flex-col gap-1">
                  {items.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setForm(fromShift(s));
                      }}
                      className="block truncate rounded-md px-1.5 py-1 text-left text-[11px] font-semibold text-white"
                      style={{ background: personColor(s.userId) }}
                    >
                      {personLabel(s)} {fmtTime(s.startsAt)}–{fmtTime(s.endsAt)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        {loading && <p className="mt-3 text-[12.5px] text-muted-3">Hleð…</p>}
        <p className="mt-3 text-[11.5px] text-muted-3">
          Tímar eru í Reykjavík. Margir geta verið á vakt á sama tíma.
        </p>
      </section>

      {form && (
        <ShiftModal
          form={form}
          people={people}
          saving={saving}
          onChange={setForm}
          onClose={() => setForm(null)}
          onSave={() => void save()}
          onDelete={form.id != null ? () => void remove() : undefined}
        />
      )}
    </div>
  );
}

function ShiftModal({
  form,
  people,
  saving,
  onChange,
  onClose,
  onSave,
  onDelete,
}: {
  form: FormState;
  people: DutyPerson[];
  saving: boolean;
  onChange: (next: FormState) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
}) {
  const options =
    form.userId && !people.some((p) => p.id === form.userId)
      ? [
          { id: form.userId, email: "", displayName: "Óvirkur notandi" },
          ...people,
        ]
      : people;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl border border-line bg-white p-5 shadow-[0_24px_60px_rgba(11,36,71,.16)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="m-0 font-display text-[18px] font-extrabold text-ink">
              {form.id == null ? "Ný vakt" : "Breyta vakt"}
            </h2>
            <p className="m-0 mt-1 text-[12px] text-muted-2">
              Tímar í Reykjavík
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="cursor-pointer text-[18px] text-muted-2 hover:text-ink"
          >
            ✕
          </button>
        </div>

        <label className="mb-3 block">
          <span className={label}>Notandi</span>
          <select
            value={form.userId}
            onChange={(e) => onChange({ ...form, userId: e.target.value })}
            className={input}
          >
            {options.length === 0 && (
              <option value="">Enginn virkur notandi</option>
            )}
            {options.map((p) => (
              <option key={p.id} value={p.id}>
                {personLabel(p)}
              </option>
            ))}
          </select>
        </label>

        <div className="mb-3 grid grid-cols-2 gap-3">
          <label>
            <span className={label}>Upphaf</span>
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => onChange({ ...form, startsAt: e.target.value })}
              className={input}
            />
          </label>
          <label>
            <span className={label}>Lok</span>
            <input
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => onChange({ ...form, endsAt: e.target.value })}
              className={input}
            />
          </label>
        </div>

        <label className="mb-5 block">
          <span className={label}>Athugasemd</span>
          <input
            value={form.note}
            maxLength={200}
            onChange={(e) => onChange({ ...form, note: e.target.value })}
            className={input}
          />
        </label>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !form.userId}
            className="h-10 cursor-pointer rounded-xl bg-ink px-4 text-sm font-bold text-white disabled:opacity-50"
          >
            {saving ? "Vista…" : "Vista"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="h-10 cursor-pointer rounded-xl border border-line px-4 text-sm font-semibold text-ink"
          >
            Hætta við
          </button>
          {onDelete && (
            <button
              type="button"
              onClick={onDelete}
              disabled={saving}
              className="ml-auto h-10 cursor-pointer rounded-xl px-4 text-sm font-semibold text-danger disabled:opacity-50"
            >
              Eyða
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
